import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Form, Input, Button, Card, message, Select, Typography, Space, Spin } from 'antd';
import { UserOutlined, LockOutlined, GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import BrandLogo from '../../components/common/BrandLogo';
import { postAuthWorkspacePath } from '../../utils/onboardingStep';

const { Title } = Typography;
const { Option } = Select;

export const Login = () => {
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation(['auth', 'common']);
  const { firebaseUser, firestoreUser, loading: authLoading, signIn } = useAuth();
  const location = useLocation();
  const [form] = Form.useForm();

  // Handle state from invitation acceptance
  useEffect(() => {
    if (location.state?.email) {
      form.setFieldsValue({ email: location.state.email });
    }
    if (location.state?.message) {
      message.success(location.state.message);
    }
  }, [location.state, form]);

  // Session restore: wait for profile, then onboarding (if not finished) or dashboard
  if (firebaseUser && authLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (firebaseUser) {
    if (!firestoreUser?.workspace?.id) {
      return <Navigate to="/select-workspace" replace />;
    }
    return <Navigate to={postAuthWorkspacePath(firestoreUser.workspace)} replace />;
  }

  const onFinish = async (values: { email: string; password: string }) => {
    try {
      setLoading(true);
      await signIn(values.email, values.password);
      // Do not show success message or navigate here; let redirect happen when firebaseUser is ready
    } catch (error) {
      message.error(t('auth:loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (value: string) => {
    i18n.changeLanguage(value);
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center',
      background: '#f0f2f5'
    }}>
      <Space direction="vertical" size="small" style={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>
        <Card style={{ boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <BrandLogo variant="bright" showTagline={true} />
          </div>
          <Title level={4} style={{ margin: 0, fontWeight: 'normal', color: '#666' }}>
            {t('auth:loginTitle')}
          </Title>
          <br/>
          <Form
            form={form}
            name="login"
            initialValues={{ remember: true }}
            onFinish={onFinish}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: t('auth:emailRequired') },
                { type: 'email', message: t('auth:emailInvalid') }
              ]}
            >
              <Input 
                prefix={<UserOutlined />} 
                placeholder={t('auth:emailPlaceholder')} 
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: t('auth:passwordRequired') }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t('auth:passwordPlaceholder')}
              />
            </Form.Item>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                block
              >
                {t('auth:loginButton')}
              </Button>
            </Form.Item>
            <div style={{ marginTop: 16 }}>
              <a href="/register">{t('auth:noAccountRegister')}</a>
            </div>
          </Form>
        </Card>

        <div>
          <GlobalOutlined style={{ marginRight: 8 }} />
          <Select
            value={i18n.language}
            onChange={handleLanguageChange}
            style={{ width: 140 }}
          >
            <Option value="en">English</Option>
            <Option value="es">Español</Option>
            <Option value="pt">Português</Option>
          </Select>
        </div>
      </Space>
    </div>
  );
}; 